import { useCallback, useEffect, useState } from "react";
import { useManagerScope } from "@/components/ManagerLayout";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, usePagination } from "@/components/Pagination";

const ACTIONS = ["ALL", "CLOCK_IN", "CLOCK_OUT", "SHIFT_APPROVED", "SHIFT_FLAGGED",
  "SHIFT_TIME_EDITED", "SHIFT_CLOCKOUT_ADDED", "LEAVE_APPROVED", "LEAVE_DECLINED",
  "LEAVE_REQUESTED", "UNAUTHORISED_ACCESS", "SITE_SCOPE_VIOLATION"];

function fmtDateTime(iso) { return new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }

export default function ManagerActivity() {
  const [items, setItems] = useState([]);
  const [action, setAction] = useState("ALL");
  const { scopeToken } = useManagerScope();

  const load = useCallback(async () => {
    const params = {};
    if (action !== "ALL") params.action = action;
    const { data } = await api.get("/manager/reports/activity", { params });
    setItems(data.items);
  }, [action]);
  useEffect(() => { load(); }, [load, scopeToken]);

  const pg = usePagination(items, 10);

  return (
    <div className="space-y-6" data-testid="mgr-activity-page">
      <header>
        <p className="text-[10px] text-[#C9A84C] uppercase tracking-[0.25em] font-semibold">Activity</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744] mt-1">Site activity log</h1>
        <p className="text-sm text-gray-500 mt-1">Everything that happened at your site. Sensitive fields (rates, passwords) are never shown.</p>
      </header>

      <div className="flex items-center gap-2">
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-56" data-testid="activity-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Summary</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">No activity for this filter.</TableCell></TableRow>
              )}
              {pg.pageItems.map((it) => (
                <TableRow key={it.id} data-testid={`activity-row-${it.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(it.created_at)}</TableCell>
                  <TableCell className="text-sm">{it.actor_name || it.actor_id || "system"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      it.action === "UNAUTHORISED_ACCESS" || it.action === "SITE_SCOPE_VIOLATION" ? "bg-red-50 text-red-800 border-red-200" :
                      it.action.includes("APPROVED") ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                      "bg-gray-50 text-gray-700 border-gray-200"
                    }>{it.action}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-gray-600">{it.entity_type}{it.entity_id ? ` · ${it.entity_id.slice(0, 8)}…` : ""}</TableCell>
                  <TableCell className="text-xs text-gray-500 max-w-[300px] truncate">
                    {it.after ? JSON.stringify(it.after).slice(0, 120) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="mgr-activity" />
    </div>
  );
}
